
import React from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EstimateStratigraphy } from '@/hooks/useEstimateStratigraphies';
import EstimateStratigraphiesEmptyState from './components/EstimateStratigraphiesEmptyState';
import EstimateStratigraphyRow from './components/EstimateStratigraphyRow';
import ReconnectStratigraphyDialog from './ReconnectStratigraphyDialog';
import SnapshotViewerDialog from './SnapshotViewerDialog';
import { EstimateStratigraphySortField, SortDirection } from '@/hooks/useEstimateStratigraphiesSorting';
import { ArrowUp, ArrowDown, ArrowUpDown, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { useOrgProfile } from '@/hooks/useOrgSettings';
import { mergeEstimatePricing } from '@/utils/pricing/mergeEstimatePricing';
import { applyPricingForRows } from '@/utils/pricing/applyPricingForRows';
import { computeEstimateCostBase, computeEstimateRowCosts, rowCostOptionsFrom } from '@/utils/pricing/computeEstimateCostBase';
import { distributeSellingPrice, type RowRevenue } from '@/utils/pricing/distributeSellingPrice';
import type { EstimateRowCost } from '@/utils/pricing/computeEstimateCostBase';
import { isEstimateLocked } from '@/utils/estimates/estimateLock';
import { useFinishLevels } from '@/hooks/useFinishLevels';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatEuro } from '@/utils/format';

interface EstimateStratigraphiesTableProps {
  stratigraphies: (EstimateStratigraphy & { stratigraphy?: any })[];
  onUpdateStratigraphy: (id: string, data: any) => void;
  /** Cambia il livello di finitura (Q1-Q4) di una riga. null = nessuna finitura. */
  onUpdateFinishLevel?: (id: string, finishCode: string | null) => void;
  onDeleteStratigraphy: (id: string) => void;
  onUpdatePrices?: (estimateStratigraphyId: string, originalStratigraphyId: string) => void;
  isUpdating: boolean;
  isDeleting: boolean;
  isUpdatingPrices?: boolean;
  estimateStatus?: string;
  // nuovi props per sorting
  sortField: EstimateStratigraphySortField;
  sortDirection: SortDirection;
  onSortChange: (field: EstimateStratigraphySortField) => void;
  /** Override pricing del preventivo, per calcolare i ricavi di riga. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pricingOverrides?: any;
}

const EstimateStratigraphiesTable = ({
  stratigraphies,
  onUpdateStratigraphy,
  onUpdateFinishLevel,
  onDeleteStratigraphy,
  onUpdatePrices,
  isUpdating,
  isDeleting,
  isUpdatingPrices = false,
  estimateStatus = 'draft',
  sortField,
  sortDirection,
  onSortChange,
  pricingOverrides = null,
}: EstimateStratigraphiesTableProps) => {
  // Livelli finitura disponibili per l'org (Q1-Q4). Servono a popolare il
  // dropdown della colonna Finitura in ogni riga.
  const { data: finishLevels = [] } = useFinishLevels();
  const finishLevelCodes = finishLevels.map((l) => l.code);
  // Tariffa oraria (globale) per scomporre manodopera/materiali nelle colonne dedicate.
  const { data: costPerHour = 30 } = useQuery({
    queryKey: ['cost_per_hour'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from('configurator_settings')
        .select('value')
        .eq('key', 'cost_per_hour')
        .maybeSingle();
      return parseFloat((data as { value?: string } | null)?.value ?? '30') || 30;
    },
  });
  // ===== RICAVI PER RIGA =====
  // Il prezzo di vendita resta calcolato UNA volta sull'intero preventivo:
  // qui viene solo ripartito sulle righe, con gli stessi coefficienti. La
  // somma della colonna "Ricavo & Margine" e` quindi identica al prezzo lordo
  // della card "Vista vendita". Visibile solo a chi puo` vedere i margini.
  const { currentRole } = useCurrentOrganization();
  const canSeeMargins = currentRole === 'owner' || currentRole === 'admin';
  const { data: org } = useOrgProfile();
  const [showRevenue, setShowRevenue] = React.useState(false);
  const showRevenueCols = canSeeMargins && showRevenue;
  const effectivePricing = React.useMemo(
    () => mergeEstimatePricing(org ?? null, pricingOverrides),
    [org, pricingOverrides],
  );
  const costOpts = React.useMemo(() => rowCostOptionsFrom(effectivePricing), [effectivePricing]);
  // Costi per riga: servono SEMPRE, perche' sfrido e smaltimento sono colonne
  // fisse. Stessa funzione che alimenta la Vista vendita, quindi i numeri della
  // tabella e quelli della card coincidono per costruzione.
  const rowCosts = React.useMemo(
    () => computeEstimateRowCosts(stratigraphies, costOpts),
    [stratigraphies, costOpts],
  );
  const costById = React.useMemo(() => {
    const map = new Map<string, EstimateRowCost>();
    for (const r of rowCosts) if (r.id) map.set(r.id, r);
    return map;
  }, [rowCosts]);
  const totaliCosti = React.useMemo(() => rowCosts.reduce(
    (acc, r) => ({ waste: acc.waste + r.waste, disposal: acc.disposal + r.disposal }),
    { waste: 0, disposal: 0 },
  ), [rowCosts]);
  const revenueById = React.useMemo(() => {
    const map = new Map<string, RowRevenue>();
    if (!showRevenueCols || stratigraphies.length === 0) return map;
    const breakdown = applyPricingForRows(
      computeEstimateCostBase(stratigraphies, costOpts),
      effectivePricing,
      stratigraphies,
    );
    for (const r of distributeSellingPrice(rowCosts, breakdown)) {
      if (r.id) map.set(r.id, r);
    }
    return map;
  }, [stratigraphies, effectivePricing, showRevenueCols, costOpts, rowCosts]);

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editData, setEditData] = React.useState<any>({});
  // Stato dialog "Ricollega al catalogo" per row orfane (originale eliminata).
  const [reconnectItem, setReconnectItem] = React.useState<
    (EstimateStratigraphy & { stratigraphy?: any }) | null
  >(null);
  // Stato dialog "Visualizza snapshot" (read-only) per row orfane: l'icona
  // Eye non può aprire il configuratore (id catalogo NULL) → snapshot viewer.
  const [snapshotItem, setSnapshotItem] = React.useState<
    (EstimateStratigraphy & { stratigraphy?: any }) | null
  >(null);

  const canEdit = !isEstimateLocked(estimateStatus);

  const handleStartEdit = (stratigraphy: EstimateStratigraphy) => {
    setEditingId(stratigraphy.id);
    setEditData({
      name: stratigraphy.name,
      area: stratigraphy.area,
      // unitCost editabile SOLO sulle row orfane (originale eliminata): la
      // row component lo mostra come input quando isSnapshot=true e
      // originalStratigraphyId è null. Per le altre row il campo è incluso
      // ma ignorato dalla UI (read-only display).
      unitCost: stratigraphy.unitCost,
      // F32: altezza parete (in metri). Se non valorizzata nel DB, mostriamo
      // 2.7 come placeholder. Il salvataggio passa via wall_height in DB.
      wallHeight: (stratigraphy as { wallHeight?: number }).wallHeight ?? null,
    });
  };

  const handleSaveEdit = () => {
    if (editingId) {
      onUpdateStratigraphy(editingId, editData);
      setEditingId(null);
      setEditData({});
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const getSortIcon = (field: EstimateStratigraphySortField) => {
    if (sortField !== field) return <ArrowUpDown size={16} className="inline text-zinc-300 ml-1" />;
    if (sortDirection === 'asc') return <ArrowUp size={16} className="inline text-primary ml-1" />;
    return <ArrowDown size={16} className="inline text-primary ml-1" />;
  };

  const handleSort = (field: EstimateStratigraphySortField) => {
    onSortChange(field);
  };

  if (stratigraphies.length === 0) {
    return <EstimateStratigraphiesEmptyState />;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {canSeeMargins && (
        <div className="flex items-center justify-end gap-2 border-b bg-gray-50/60 px-3 py-1.5">
          <span className="text-xs text-muted-foreground">
            {showRevenue ? 'Ricavi e margini per riga' : 'Solo costi'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={() => setShowRevenue((v) => !v)}
            title="Mostra o nascondi le colonne di ricavo. Il prezzo di vendita e` un dato interno: non compare mai nella tabella stampata per il cliente."
          >
            {showRevenue ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {showRevenue ? 'Nascondi ricavi' : 'Mostra ricavi'}
          </Button>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="w-24">Anteprima</TableHead>
            <TableHead
              role="button"
              className="cursor-pointer select-none"
              onClick={() => handleSort('name')}
            >
              Nome & Dettagli {getSortIcon('name')}
            </TableHead>
            <TableHead>Tipo & Specifiche</TableHead>
            <TableHead
              role="button"
              className="cursor-pointer select-none"
              onClick={() => handleSort('area')}
            >
              Superficie (m²) {getSortIcon('area')}
            </TableHead>
            {/* Intestazione "Altezza (m)" rimossa su richiesta (non incide sul costo;
                cella corrispondente rimossa in EstimateStratigraphyRow). */}
            <TableHead className="w-32" title="Livello di finitura (Q1-Q4) applicato alla riga. Cambiarlo ricalcola costo e manodopera della finitura.">
              Finitura
            </TableHead>
            <TableHead
              role="button"
              className="cursor-pointer select-none"
              onClick={() => handleSort('unitCost')}
              title="Costo al m² della riga, maggiorazione per difficoltà INCLUSA: moltiplicato per la superficie dà il Totale. Quando la difficoltà è diversa da 1 il costo base è indicato sotto. L'ordinamento usa il costo base."
            >
              Costo Unitario {getSortIcon('unitCost')}
            </TableHead>
            <TableHead
              className="w-24"
              title="Coefficiente di maggiorazione della MANODOPERA per riga (1.00 = neutro). Es: 1.25 pareti alte, 1.30 accesso difficile. Aumenta solo il tempo/mano d'opera, NON i materiali. Non è mark-up."
            >
              Difficoltà
            </TableHead>
            <TableHead className="w-28" title="Costo manodopera della riga (base + finitura) × difficoltà. La difficoltà incide solo qui, non sui materiali.">
              Costo Manodopera
            </TableHead>
            <TableHead className="w-28" title="Costo dello sfrido: il materiale in più che acquisti per i ritagli. Percentuale del materiale, altrimenti default di categoria da Impostazioni → Sfridi. Entra nei costi diretti, quindi nel prezzo.">
              Costo Sfrido
            </TableHead>
            <TableHead className="w-28" title="Oneri di discarica sui materiali, sfrido compreso. Percentuale dell'organizzazione, sovrascrivibile sul singolo materiale. Distinto dal forfait di cantiere delle Impostazioni.">
              Costo Smaltimento
            </TableHead>
            <TableHead className="w-28" title="Costo materiali della riga: lastre/isolanti + viti + materiali di finitura. Non influenzato dalla difficoltà.">
              Costo Materiale
            </TableHead>
            <TableHead
              role="button"
              className="cursor-pointer select-none"
              onClick={() => handleSort('totalCost')}
            >
              Totale {getSortIcon('totalCost')}
            </TableHead>
            {showRevenueCols && (
              <>
                <TableHead className="w-28 bg-emerald-50/60" title="Ricavo attribuito alla manodopera: costo manodopera (posa + finitura, difficolta` inclusa) con il ricarico manodopera applicato.">
                  Ricavo Manodopera
                </TableHead>
                <TableHead className="w-28 bg-emerald-50/60" title="Ricavo attribuito ai materiali, profilo perimetrale incluso, col ricarico materiali applicato.">
                  Ricavo Materiale
                </TableHead>
                <TableHead className="w-28 bg-emerald-50/60" title="Prezzo di vendita al metro quadro di QUESTA riga, difficolta` inclusa. E` il valore che finisce nel PDF cliente.">
                  Ricavo €/m²
                </TableHead>
                <TableHead className="w-32 bg-emerald-50/60" title="Ricavo totale della riga e margine LORDO, in percentuale sul ricavo. La somma di questa colonna è esattamente il prezzo lordo del preventivo. Il margine NETTO della card Vista vendita è più basso: toglie anche i costi accessori.">
                  Ricavo & Margine lordo
                </TableHead>
              </>
            )}
            <TableHead className="w-28" title="Tempo di manodopera totale (base stratigrafia + finitura) × superficie × difficoltà. Cambia al variare della finitura o della difficoltà.">
              Tempo Totale
            </TableHead>
            {/* Colonne Margine e IVA nascoste (non utilizzate, i default org restano
                validi nel calcolo prezzo di vendita). Per riattivarle togliere il commento
                qui e nella cella corrispondente in EstimateStratigraphyRow.
            <TableHead className="w-28" title="Mark-up % override su questa riga. 'Default' usa i mark-up categoria dell'organizzazione.">
              Margine
            </TableHead>
            <TableHead className="w-24" title="Aliquota IVA per questa riga. 'Default' usa quella dell'organizzazione.">
              IVA
            </TableHead>
            */}
            <TableHead
              role="button"
              className="cursor-pointer select-none"
              onClick={() => handleSort('pricesUpdatedAt')}
            >
              Prezzi Aggiornati {getSortIcon('pricesUpdatedAt')}
            </TableHead>
            <TableHead className="w-28">Azioni</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {stratigraphies.map((item) => (
            <EstimateStratigraphyRow
              key={item.id}
              item={item}
              editingId={editingId}
              editData={editData}
              canEdit={canEdit}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
              isUpdatingPrices={isUpdatingPrices}
              onStartEdit={handleStartEdit}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              onDelete={onDeleteStratigraphy}
              onUpdatePrices={onUpdatePrices}
              onEditDataChange={setEditData}
              onReconnect={setReconnectItem}
              onViewSnapshot={setSnapshotItem}
              finishLevelCodes={finishLevelCodes}
              onUpdateFinishLevel={onUpdateFinishLevel}
              costPerHour={costPerHour}
              rowCost={costById.get(item.id)}
              revenue={revenueById.get(item.id)}
              showRevenue={showRevenueCols}
              onUpdateVatRate={(id, rate) => onUpdateStratigraphy(id, { vatRate: rate })}
              onUpdateMarkupPct={(id, pct) => onUpdateStratigraphy(id, { markupPct: pct })}
              onUpdateDifficultyMultiplier={(id, mult) => onUpdateStratigraphy(id, { difficultyMultiplier: mult })}
            />
          ))}
        </TableBody>
      </Table>
      {/* Riepilogo dei due costi nuovi: sono gli stessi euro che compaiono
          nella Vista vendita dentro "Materiali". */}
      <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 border-t bg-gray-50/60 px-3 py-2 text-xs">
        <span className="text-muted-foreground">Totale sfrido</span>
        <span className="font-mono font-semibold text-amber-800 tabular-nums">{formatEuro(totaliCosti.waste)}</span>
        <span className="text-muted-foreground">Totale smaltimento</span>
        <span className="font-mono font-semibold text-amber-800 tabular-nums">{formatEuro(totaliCosti.disposal)}</span>
        <span className="text-muted-foreground italic">
          già inclusi nei costi diretti della Vista vendita
        </span>
      </div>

      <ReconnectStratigraphyDialog
        open={!!reconnectItem}
        onOpenChange={(open) => { if (!open) setReconnectItem(null); }}
        item={reconnectItem}
      />

      <SnapshotViewerDialog
        open={!!snapshotItem}
        onOpenChange={(open) => { if (!open) setSnapshotItem(null); }}
        item={snapshotItem}
        onReconnect={() => {
          if (snapshotItem) {
            setReconnectItem(snapshotItem);
            setSnapshotItem(null);
          }
        }}
      />
    </div>
  );
};

export default EstimateStratigraphiesTable;
